import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { 
  XMarkIcon, 
  PlusIcon, 
  TrashIcon,
  UserIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { contactService } from '../../services/ContactService';
import { toast } from 'react-toastify';

function ContactFormModal({ isOpen, onClose, contactToEdit, uid }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    type: 'person',
    name: '',
    surname: '',
    jobTitle: '',
    relatedAccountId: '',
    relatedAccountName: '',
    emailAddresses: [],
    phoneNumbers: [],
    webPage: '',
    birthday: '',
    postalAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: ''
    },
    notes: '',
    imageURL: ''
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const loadOrganizations = useCallback(async () => {
    try {
      const orgs = await contactService.getOrganizations(uid);
      setOrganizations(orgs);
    } catch (error) {
      console.error('Error loading organizations:', error);
    }
  }, [uid]);

  // Reset or Populate form
  useEffect(() => {
    if (isOpen) {
      // ... existing code ...
      
      // Load organizations for dropdown
      loadOrganizations();
    }
  }, [isOpen, contactToEdit, uid, loadOrganizations]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        postalAddress: {
          ...prev.postalAddress,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleArrayChange = (index, field, value) => {
    setFormData(prev => {
      const newArray = [...prev[field]];
      newArray[index] = value;
      return { ...prev, [field]: newArray };
    });
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (index, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleOrganizationSelect = (e) => {
    const orgId = e.target.value;
    if (!orgId) {
      setFormData(prev => ({ ...prev, relatedAccountId: '', relatedAccountName: '' }));
      return;
    }
    const org = organizations.find(o => o.id === orgId);
    setFormData(prev => ({ 
      ...prev, 
      relatedAccountId: orgId, 
      relatedAccountName: org ? org.name : '' 
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error(t('contacts.imageSizeLimitError'));
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (contactToEdit) {
        // Mark for removal in service logic
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error(t('contacts.nameRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare data
      const dataToSave = {
        ...formData,
        birthday: formData.birthday ? new Date(formData.birthday) : null
      };
      
      // Clean empty array items
      dataToSave.emailAddresses = dataToSave.emailAddresses.filter(e => e.trim());
      dataToSave.phoneNumbers = dataToSave.phoneNumbers.filter(p => p.trim());

      if (contactToEdit) {
        // Check if image was removed
        const removeImage = contactToEdit.imageURL && !imagePreview;
        
        await contactService.updateContact(
          uid, 
          contactToEdit.id, 
          dataToSave, 
          imageFile, 
          removeImage
        );
        toast.success(t('contacts.contactUpdatedSuccessfully'));
      } else {
        await contactService.createContact(uid, dataToSave, imageFile);
        toast.success(t('contacts.contactCreatedSuccessfully'));
      }
      onClose();
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error(t('contacts.failedToSaveContact'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60] flex items-center justify-center p-4">
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">
            {contactToEdit ? t('contacts.editContact') : t('contacts.createNewContact')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form id="contact-form" onSubmit={handleSubmit} className="space-y-6">
            
            {/* Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('contacts.contactType')} *</label>
              <div className="flex items-center space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="type"
                    value="person"
                    checked={formData.type === 'person'}
                    onChange={handleChange}
                    className="form-radio text-orange-600 focus:ring-orange-500"
                  />
                  <span className="ml-2 flex items-center"><UserIcon className="w-4 h-4 mr-1"/> {t('contacts.person')}</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="type"
                    value="organization"
                    checked={formData.type === 'organization'}
                    onChange={handleChange}
                    className="form-radio text-orange-600 focus:ring-orange-500"
                  />
                  <span className="ml-2 flex items-center"><BuildingOfficeIcon className="w-4 h-4 mr-1"/> {t('contacts.organization')}</span>
                </label>
              </div>
            </div>

            {/* Image Upload */}
            <div className="flex items-center space-x-4">
              <div className="relative h-20 w-20 rounded-full overflow-hidden bg-gray-100 border border-gray-200">
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-gray-400">
                    {formData.type === 'person' ? <UserIcon className="w-10 h-10"/> : <BuildingOfficeIcon className="w-10 h-10"/>}
                  </div>
                )}
              </div>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {imagePreview ? t('common.change') : t('common.uploadPhoto')}
                  </button>
                  {imagePreview && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="px-3 py-2 border border-red-200 rounded-md text-sm font-medium text-red-600 hover:bg-red-50"
                    >
                      {t('contacts.remove')}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">{t('contacts.name')} *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm border p-2"
                  placeholder={formData.type === 'person' ? t('contacts.nameLabelPerson') : t('contacts.nameLabelOrganization')}
                  required
                />
              </div>

              {formData.type === 'person' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('contacts.surname')}</label>
                    <input
                      type="text"
                      name="surname"
                      value={formData.surname}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm border p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('contacts.jobTitle')}</label>
                    <input
                      type="text"
                      name="jobTitle"
                      value={formData.jobTitle}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm border p-2"
                    />
                  </div>
                  <div>
                     <label className="block text-sm font-medium text-gray-700">{t('contacts.organizationLabel')}</label>
                     <select
                        name="relatedAccountId"
                        value={formData.relatedAccountId}
                        onChange={handleOrganizationSelect}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm border p-2 bg-white"
                     >
                        <option value="">{t('contacts.selectOrganization')}</option>
                        {organizations.map(org => (
                            <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                     </select>
                  </div>
                   <div>
                    <label className="block text-sm font-medium text-gray-700">{t('contacts.birthday')}</label>
                    <input
                      type="date"
                      name="birthday"
                      value={formData.birthday}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm border p-2"
                    />
                  </div>
                </>
              )}
            </div>

             {/* Dynamic Fields: Emails */}
             <div>
                <div className="flex justify-between items-center mb-2">
                   <label className="block text-sm font-medium text-gray-700">{t('contacts.emailAddresses')}</label>
                   <button type="button" onClick={() => addArrayItem('emailAddresses')} className="text-sm text-orange-600 hover:text-orange-700 flex items-center">
                     <PlusIcon className="w-4 h-4 mr-1"/> {t('contacts.addEmail')}
                   </button>
                </div>
                {formData.emailAddresses.map((email, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => handleArrayChange(index, 'emailAddresses', e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm border p-2"
                            placeholder="email@example.com"
                        />
                        <button type="button" onClick={() => removeArrayItem(index, 'emailAddresses')} className="text-red-500 hover:text-red-700">
                            <TrashIcon className="w-5 h-5" />
                        </button>
                    </div>
                ))}
                 {formData.emailAddresses.length === 0 && (
                     <p className="text-xs text-gray-400 italic">{t('contacts.noEmailsAdded')}</p>
                 )}
             </div>

             {/* Dynamic Fields: Phones */}
             <div>
                <div className="flex justify-between items-center mb-2">
                   <label className="block text-sm font-medium text-gray-700">{t('contacts.phoneNumbers')}</label>
                   <button type="button" onClick={() => addArrayItem('phoneNumbers')} className="text-sm text-orange-600 hover:text-orange-700 flex items-center">
                     <PlusIcon className="w-4 h-4 mr-1"/> {t('contacts.addPhone')}
                   </button>
                </div>
                {formData.phoneNumbers.map((phone, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                        <input
                            type="tel"
                            value={phone}
                            onChange={(e) => handleArrayChange(index, 'phoneNumbers', e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm border p-2"
                            placeholder="+1 234 567 890"
                        />
                        <button type="button" onClick={() => removeArrayItem(index, 'phoneNumbers')} className="text-red-500 hover:text-red-700">
                            <TrashIcon className="w-5 h-5" />
                        </button>
                    </div>
                ))}
                {formData.phoneNumbers.length === 0 && (
                     <p className="text-xs text-gray-400 italic">{t('contacts.noPhoneNumbersAdded')}</p>
                 )}
             </div>

             {/* Web Page */}
             <div>
                <label className="block text-sm font-medium text-gray-700">{t('contacts.website')}</label>
                <input
                    type="url"
                    name="webPage"
                    value={formData.webPage}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm border p-2"
                    placeholder="https://example.com"
                />
             </div>

             {/* Address */}
             <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">{t('contacts.postalAddress')}</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="col-span-2">
                        <input
                            type="text"
                            name="address.street"
                            value={formData.postalAddress.street}
                            onChange={handleChange}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm border p-2"
                            placeholder={t('contacts.street')}
                        />
                    </div>
                    <input
                        type="text"
                        name="address.city"
                        value={formData.postalAddress.city}
                        onChange={handleChange}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm border p-2"
                        placeholder={t('contacts.city')}
                    />
                    <input
                        type="text"
                        name="address.state"
                        value={formData.postalAddress.state}
                        onChange={handleChange}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm border p-2"
                        placeholder={t('contacts.state')}
                    />
                    <input
                        type="text"
                        name="address.postalCode"
                        value={formData.postalAddress.postalCode}
                        onChange={handleChange}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm border p-2"
                        placeholder={t('contacts.postalCode')}
                    />
                    <input
                        type="text"
                        name="address.country"
                        value={formData.postalAddress.country}
                        onChange={handleChange}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm border p-2"
                        placeholder={t('contacts.country')}
                    />
                </div>
             </div>

             {/* Notes */}
             <div>
                <label className="block text-sm font-medium text-gray-700">{t('contacts.notes')}</label>
                <textarea
                    name="notes"
                    rows={3}
                    value={formData.notes}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm border p-2"
                    placeholder={t('contacts.additionalNotes')}
                />
             </div>

          </form>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end space-x-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form="contact-form"
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
          >
            {isSubmitting ? t('common.saving') : `${t('common.save')} ${t('common.contact')}`}
          </button>
        </div>
      </div>
    </div>
  );
}

ContactFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contactToEdit: PropTypes.object,
  uid: PropTypes.string.isRequired
};

export default ContactFormModal;

